import type { ProceduralTextureSpec } from "./ThemeManifest.js";
import { hexToNumber } from "./hexColor.js";

/**
 * Phaser.GameObjects.Graphics 중 사용하는 부분만의 구조 타입 — Phaser 비의존이라
 * 패키지가 순수 TS로 유지되고 단위테스트에서 mock 주입이 가능하다(Phaser.Scene은 구조적으로 호환).
 */
export interface ProceduralGraphics {
  clear(): unknown;
  fillStyle(color: number, alpha?: number): unknown;
  lineStyle(width: number, color: number, alpha?: number): unknown;
  fillRoundedRect(x: number, y: number, width: number, height: number, radius: number): unknown;
  strokeRoundedRect(x: number, y: number, width: number, height: number, radius: number): unknown;
  fillCircle(x: number, y: number, radius: number): unknown;
  fillRect(x: number, y: number, width: number, height: number): unknown;
  generateTexture(key: string, width: number, height: number): unknown;
  destroy(): void;
}

/** Phaser.Scene 중 graphics 팩토리만의 구조 타입. */
export interface ProceduralScene {
  add: { graphics(): ProceduralGraphics };
}

/** spec의 텍스처 캔버스 크기. file kind는 절차 생성 대상이 아니므로 null. */
export function proceduralTextureSize(
  spec: ProceduralTextureSpec,
): { width: number; height: number } | null {
  switch (spec.kind) {
    case "roundedRect":
    case "rect":
      return { width: spec.width, height: spec.height };
    case "circle":
      return { width: spec.radius * 2, height: spec.radius * 2 };
    case "file":
      return null;
  }
}

/** preload 단계에서 load.image로 처리할 file-kind 항목 추출 (절차 생성 대신 PNG 대체분). */
export function proceduralFileEntries(
  specs: Readonly<Record<string, ProceduralTextureSpec>>,
): ReadonlyArray<{ key: string; path: string }> {
  return Object.entries(specs).flatMap(([key, spec]) =>
    spec.kind === "file" ? [{ key, path: spec.path }] : [],
  );
}

/** spec 1건을 Graphics에 그린다. file kind는 no-op (preload에서 처리). */
export function drawProceduralSpec(g: ProceduralGraphics, spec: ProceduralTextureSpec): void {
  switch (spec.kind) {
    case "roundedRect": {
      g.fillStyle(hexToNumber(spec.fill), 1);
      g.fillRoundedRect(0, 0, spec.width, spec.height, spec.radius);
      if (spec.stroke) {
        // 선은 path 중심으로 그려지므로 선폭 절반만큼 안쪽으로 — 캔버스 밖 잘림 방지.
        const inset = spec.stroke.width / 2;
        g.lineStyle(spec.stroke.width, hexToNumber(spec.stroke.color), 1);
        g.strokeRoundedRect(
          inset,
          inset,
          spec.width - spec.stroke.width,
          spec.height - spec.stroke.width,
          spec.radius,
        );
      }
      break;
    }
    case "circle":
      g.fillStyle(hexToNumber(spec.fill), 1);
      g.fillCircle(spec.radius, spec.radius, spec.radius);
      break;
    case "rect":
      g.fillStyle(hexToNumber(spec.fill), 1);
      g.fillRect(0, 0, spec.width, spec.height);
      break;
    case "file":
      break;
  }
}

/**
 * file-kind 외 spec 전부를 Graphics 1개로 그려 generateTexture — BootScene.create()에서 1회 호출.
 * 키 순회 순서는 매니페스트 정의 순서를 따른다.
 */
export function generateProceduralTextures(
  scene: ProceduralScene,
  specs: Readonly<Record<string, ProceduralTextureSpec>>,
): void {
  const g = scene.add.graphics();
  for (const [key, spec] of Object.entries(specs)) {
    const size = proceduralTextureSize(spec);
    if (!size) continue;
    g.clear();
    drawProceduralSpec(g, spec);
    g.generateTexture(key, size.width, size.height);
  }
  g.destroy();
}
